import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { ConfigActions, EntityModalActions, ServerListActions, SettingsActions } from 'actions';
import { ACTIVITIES, AGENDA } from 'constants/Entities';
import FieldWrapper from 'containers/components/Fields/FieldWrapper';
import SelectHoi from 'components/SelectHoi';
import { VideoCallsService } from 'services';
import copyToClipboard from 'utils/copy';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { successToast } from 'utils/toast';
import { logEvent } from 'utils/tracking';
import {
    Advice,
    Avatar,
    Button,
    Icon,
    Link,
    Loader,
    Switch,
    Text,
    Tooltip,
    useTheme,
} from '@Sage/hoi-poi-ui';

import './styles.scss';

// Constants
const TEAMS_ID = 28;
const POPUP_WINDOW_FEATURES =
    'toolbar=no,menubar=no,location=no,status=no,top=100,left=100,width=600,height=700';

// Provider configuration map
const PROVIDER_CONFIG = {
    ZOOM: {
        iconName: 'zoomList',
        name: 'zoom',
        className: 'videocall-field__room-zoom',
        literalKey: 'label_zoom',
    },
    TEAMS: {
        iconName: 'microsoftTeams',
        name: 'teams',
        className: 'videocall-field__room-teams',
        literalKey: 'label_microsoft_teams',
    },
};

const VideocallField = memo(({ changeField, data, field }) => {
    const dispatch = useDispatch();
    const theme = useTheme();

    // ========================================
    // LOCAL STATE
    // ========================================
    const [isLoadingRoom, setIsLoadingRoom] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [failedRoomData, setFailedRoomData] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [options, setOptions] = useState([]);
    const [providers, setProviders] = useState([]);
    const [unfilteredOptions, setUnfilteredOptions] = useState([]);
    const [isLoadingJoin, setIsLoadingJoin] = useState(false);
    const [isRoomCreation, setIsRoomCreation] = useState(false);
    const [loadingActivation, setLoadingActivation] = useState(false);
    const [disableActivationButton, setDisableActivationButton] = useState(false);

    // ========================================
    // REFS
    // ========================================
    const areOptionsLoaded = useRef(false);
    const popUpWindow = useRef({});
    const intervalWindow = useRef({});

    // ========================================
    // FIELD PROPS
    // ========================================
    const {
        id,
        label,
        mandatory,
        readOnly,
        description,
        labelMode = 'horizontal',
        isBulkAction,
        inputAttrs,
        disabled,
        isClearable,
    } = field;

    // ========================================
    // EFFECTS
    // ========================================
    useEffect(() => {
        if (!areOptionsLoaded.current) {
            setIsLoadingOptions(true);
            Promise.all([
                dispatch(ServerListActions.getList(inputAttrs.list)),
                VideoCallsService.getVideoCallProvidersInformation(),
            ])
                .then(([list, providersData]) => {
                    setUnfilteredOptions(list);
                    setProviders(providersData);
                    if (list.length && providersData.length) {
                        const providersMap = providersData.reduce(function (obj, provider) {
                            obj[provider.providerId] = provider;
                            return obj;
                        }, {});

                        const filteredList = list.filter(function (element) {
                            return (
                                element.extprovidercfgid &&
                                providersMap[element.extprovidercfgid] &&
                                providersMap[element.extprovidercfgid].isActive
                            );
                        });
                        setOptions(filteredList);
                    } else if (list.length) {
                        setOptions(list);
                    }
                    setIsLoadingOptions(false);
                })
                .catch(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [dispatch, inputAttrs]);

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const selectValue = useMemo(() => {
        return data[id] || null;
    }, [data, id]);

    const getProviderConfig = useCallback((label) => {
        if (!label) return null;
        return PROVIDER_CONFIG[label.toUpperCase()] || null;
    }, []);

    const optionsWithIcons = useMemo(() => {
        return options.map((option) => {
            const config = getProviderConfig(option.label);
            if (!config) return option;
            return {
                ...option,
                icon: (
                    <Avatar
                        alt="providerIcon"
                        className={`videocall-field__room__header-icon ${config.className}`}
                        icon={config.iconName}
                        size="small"
                    />
                ),
            };
        });
    }, [getProviderConfig, options]);

    const getMeetingProviderValues = useCallback(
        (providerId) => {
            if (!providerId) return null;

            const actualProvider = unfilteredOptions.find(
                (current) => current.value === providerId,
            );

            if (!actualProvider?.label) return null;

            const config = getProviderConfig(actualProvider.label);
            if (!config) return null;

            let title = getLiteral('label_online_meeting_details');
            if (title?.includes('%@')) {
                title = title.replace('%@', getLiteral(config.literalKey));
            }

            return {
                iconName: config.iconName,
                name: config.name,
                providerClass: config.className,
                providerId,
                title,
            };
        },
        [getProviderConfig, unfilteredOptions],
    );

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const handleOnChange = useCallback(
        (value) => {
            if (!data.fini) return;
            let startDate = moment(data.fini).utc().format();
            startDate = startDate.slice(0, -1);
            changeField('videoCallProviderId')(value.value);
            setFailedRoomData(false);

            setIsLoadingRoom(true);
            VideoCallsService.createMeetingRoom(value.value, startDate)
                .then((roomData) => {
                    setIsLoadingRoom(false);
                    setIsRoomCreation(true);
                    changeField('videoCallMeetingDetails')(roomData);
                })
                .catch(() => {
                    setIsLoadingRoom(false);
                    setFailedRoomData(true);
                });
        },
        [changeField, data.fini],
    );

    const handleOnRemoveRoom = useCallback(() => {
        setIsRoomCreation(false);
        changeField('videoCallProviderId')(null);
        changeField('videoCallMeetingDetails')(null);
    }, [changeField]);

    const handleCopyToClipboard = useCallback(() => {
        if (!data?.videoCallMeetingDetails?.joinUrl) return;
        copyToClipboard(data.videoCallMeetingDetails.joinUrl);
        successToast({
            text: getLiteral('label_copied_to_clipboard'),
        });
        setIsCopied(true);
    }, [data]);

    const handleOnClickJoinMeeting = useCallback(() => {
        if (isRoomCreation) return;
        if (data.videoCallProviderId && data.videoCallMeetingDetails?.id) {
            setIsLoadingJoin(true);
            VideoCallsService.getMeetingRoom(
                data.videoCallProviderId,
                data.videoCallMeetingDetails.id,
            )
                .then((meetingRoomData) => {
                    if (meetingRoomData && meetingRoomData.startUrl) {
                        window.open(meetingRoomData.startUrl, '_blank');
                        dispatch(EntityModalActions.cancel());

                        if (meetingRoomData.isOwner) {
                            dispatch(
                                EntityModalActions.init({
                                    entity: ACTIVITIES,
                                    id: meetingRoomData.idGestion,
                                    labels: {
                                        deleteModalTitle: getLiteral('title_delete_activities'),
                                        error: getLiteral('label_failed_update_contact_salesforce'),
                                        success: getLiteral('succes_entityupdatedsuccessfully'),
                                        successDelete: getLiteral(
                                            'succes_entitydeletedsuccessfully',
                                        ),
                                        title: getLiteral('action_updatemanagement'),
                                    },
                                }),
                            );
                        }
                    }
                })
                .catch(() => {
                    setIsLoadingJoin(false);
                });
        }
    }, [data, dispatch, isRoomCreation]);

    const updateProviderAndPermissions = useCallback(
        (providerSelected) => {
            return new Promise((resolve, reject) => {
                const provider = providers.find(
                    (p) => p.providerId === providerSelected.providerId,
                );
                if (!provider) {
                    resolve();
                    return;
                }

                dispatch(SettingsActions.getVideoCallProvidersInformation())
                    .then((newProviders) => {
                        const finalProvider = newProviders.find(
                            (p) => p.providerId === provider.providerId,
                        );

                        if (
                            !finalProvider ||
                            (finalProvider.isActive === provider.isActive &&
                                finalProvider.accountId === provider.accountId)
                        ) {
                            resolve();
                            return;
                        }

                        // Update permissions
                        dispatch(
                            ConfigActions.updateSpecificPermissions([
                                { key: 'VideoCalls', value: true },
                            ]),
                        );

                        if (!unfilteredOptions.length || !newProviders.length) {
                            resolve();
                            return;
                        }

                        const providersMap = Object.fromEntries(
                            newProviders.map((p) => [p.providerId, p]),
                        );

                        const filteredList = unfilteredOptions.filter(
                            (el) =>
                                el.extprovidercfgid && providersMap[el.extprovidercfgid]?.isActive,
                        );

                        const finalValue = filteredList.find(
                            (item) => item.value === finalProvider.id.toString(),
                        );

                        if (!filteredList.length || !finalValue) {
                            resolve();
                            return;
                        }

                        setOptions(filteredList);
                        handleOnChange(finalValue);
                        successToast({ text: getLiteral('label_credentials_added') });
                        resolve();
                    })
                    .catch(reject);
            });
        },
        [dispatch, handleOnChange, providers, unfilteredOptions],
    );

    const authenticateToProvider = useCallback(() => {
        if (providers.length === 0) return;

        const provider =
            providers.length === 1
                ? providers[0]
                : providers.find((p) => p?.providerId === TEAMS_ID) || {};

        const { providerId } = provider;
        if (!providerId) return;

        setDisableActivationButton(true);

        // Focus existing window if open
        if (popUpWindow.current[providerId] && !popUpWindow.current[providerId].closed) {
            popUpWindow.current[providerId].focus();
            return;
        }

        // Open new auth window
        popUpWindow.current[providerId] = window.open(
            provider.authLink,
            provider.providerName,
            POPUP_WINDOW_FEATURES,
        );

        // Clear existing interval if any
        if (intervalWindow.current[providerId]) {
            clearInterval(intervalWindow.current[providerId]);
        }

        // Poll for window close
        intervalWindow.current[providerId] = setInterval(() => {
            if (popUpWindow.current[providerId].closed) {
                clearInterval(intervalWindow.current[providerId]);
                setLoadingActivation(true);

                updateProviderAndPermissions(provider).finally(() => {
                    logEvent({
                        event: AGENDA.trueName,
                        functionality: 'activateTeamsIntegration',
                    });
                    setLoadingActivation(false);
                    setDisableActivationButton(false);
                });
            }
        }, 500);
    }, [providers, updateProviderAndPermissions]);

    const onChangeToggle = useCallback(
        (bool) => {
            if (
                !bool &&
                (isRoomCreation || data?.videoCallProviderId || data?.videoCallMeetingDetails)
            ) {
                handleOnRemoveRoom();
                return;
            }
            if (options.length === 1) {
                handleOnChange(options[0]);
                return;
            }
        },
        [handleOnRemoveRoom, isRoomCreation, data, handleOnChange, options],
    );

    // ========================================
    // RENDER HELPERS
    // ========================================
    const renderRoomBox = useMemo(() => {
        if (!data?.videoCallProviderId || !data.fini || !options.length) return null;
        const provider = getMeetingProviderValues(data.videoCallProviderId);
        if (!provider) return null;

        const clipBoardText = isCopied
            ? getLiteral('label_copied_to_clipboard')
            : getLiteral('action_copy_to_clipboard');

        const buttonClasses = [
            'video-field-provider__room__button',
            provider.providerClass,
            isRoomCreation && 'video-field-provider__room__button--disabled',
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <div className="videocall-field__room">
                {isLoadingRoom && !failedRoomData && (
                    <div className="videocall-field__room__loader">
                        <Loader className="" size="large" />
                    </div>
                )}
                {!isLoadingRoom && data.videoCallMeetingDetails && (
                    <>
                        <div className="videocall-field__room__header">
                            <div className="videocall-field__room__header-content">
                                <div className="videocall-field__room__header-info">
                                    <Avatar
                                        alt="providerIcon"
                                        className={`videocall-field__room__header-icon ${provider.providerClass}`}
                                        icon={provider.iconName}
                                        size="small"
                                    />
                                    <Text
                                        className="videocall-field__room__header-title"
                                        type="caption"
                                        isTruncated
                                    >
                                        {provider.title}
                                    </Text>
                                </div>
                                {isRoomCreation && (
                                    <Icon
                                        className="videocall-field__room__header-close"
                                        name="close"
                                        onClick={handleOnRemoveRoom}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="videocall-field__room__content">
                            <Text
                                className="video-field-provider__room__content-label"
                                type="caption"
                            >
                                {getLiteral('label_onlinemeeting_link_share')}
                            </Text>
                            <div className="video-field-provider__room__content">
                                {!isRoomCreation && (
                                    <Link
                                        className="video-field-provider__room__content-value"
                                        href={data.videoCallMeetingDetails.joinUrl}
                                        target="_blank"
                                        type="caption"
                                    >
                                        {data.videoCallMeetingDetails.joinUrl}
                                    </Link>
                                )}
                                {isRoomCreation && (
                                    <Text
                                        className="video-field-provider__room__content-value video-field-provider__room__content-value--disabled"
                                        type="caption"
                                    >
                                        {data.videoCallMeetingDetails.joinUrl}
                                    </Text>
                                )}

                                <Tooltip placement="top" content={<span>{clipBoardText}</span>}>
                                    <Icon
                                        className="video-field-provider__room__content-url__clipboard"
                                        name="contentCopy"
                                        onClick={handleCopyToClipboard}
                                    />
                                </Tooltip>
                            </div>
                            <Text
                                className="video-field-provider__room__content-meeting-id"
                                type="caption"
                                isTruncated
                            >
                                {getLiteralWithParameters('label_online_meeting_id', [
                                    data.videoCallMeetingDetails.id,
                                ])}
                            </Text>
                        </div>
                        <Button
                            className={buttonClasses}
                            isDisabled={isRoomCreation}
                            isLoading={isLoadingJoin}
                            onClick={handleOnClickJoinMeeting}
                        >
                            <Avatar
                                className="videocall-field__room__header-icon"
                                icon={provider.iconName}
                                alt="providerIcon"
                                size="small"
                            />
                            <Text
                                className="video-field-provider__room__button-text"
                                color={theme.colors.primary.white}
                            >
                                {getLiteral('action_join_meeting')}
                            </Text>
                        </Button>
                    </>
                )}
            </div>
        );
    }, [
        data,
        failedRoomData,
        getMeetingProviderValues,
        handleCopyToClipboard,
        handleOnClickJoinMeeting,
        handleOnRemoveRoom,
        isCopied,
        isLoadingJoin,
        isLoadingRoom,
        isRoomCreation,
        options,
        theme,
    ]);

    const renderButton = useMemo(
        () => (
            <Button
                className="videocall-field__activation-button"
                isDisabled={disableActivationButton}
                isLoading={loadingActivation}
                onClick={authenticateToProvider}
                size="small"
            >
                {getLiteral('label_teams_promote_integration_calendar')}
            </Button>
        ),
        [authenticateToProvider, disableActivationButton, loadingActivation],
    );

    const renderToggle = useMemo(
        () => (
            <div className="videocall-field__switch">
                <Switch checked={!!selectValue} onChange={onChangeToggle} />
            </div>
        ),
        [onChangeToggle, selectValue],
    );

    const renderError = useMemo(
        () => (
            <Advice showCollapse={false} type="error">
                <Text>{getLiteral('error_accessing_online_provider')}</Text>
            </Advice>
        ),
        [],
    );

    // Derived render conditions
    const shouldRenderSelect = !data?.videoCallProviderId || failedRoomData;
    const shouldRenderRoom = options.length > 0 && !shouldRenderSelect && !failedRoomData;
    const shouldRenderError = !isLoadingRoom && failedRoomData;

    // ========================================
    // RENDER
    // ========================================
    if (inputAttrs?.shouldRenderField && !inputAttrs?.shouldRenderField('videoCallProviderId')) {
        failedRoomData && setFailedRoomData(false);
        return null;
    }

    if (!providers.length) return null;

    return (
        <FieldWrapper
            className="videocall-field-container"
            description={description}
            isBulkAction={isBulkAction}
            label={label}
            labelMode={labelMode}
            mandatory={mandatory}
        >
            {shouldRenderSelect && (
                <>
                    {options.length === 0 && renderButton}
                    {options.length === 1 && renderToggle}
                    {options.length > 1 && (
                        <SelectHoi
                            isClearable={isClearable}
                            isDisabled={disabled}
                            isFullWidth
                            isLoading={isLoadingOptions}
                            isReadOnly={readOnly}
                            onChange={handleOnChange}
                            options={optionsWithIcons}
                            value={selectValue}
                        />
                    )}
                </>
            )}
            {shouldRenderRoom && <div>{renderRoomBox}</div>}
            {shouldRenderError && renderError}
        </FieldWrapper>
    );
});

VideocallField.displayName = 'VideocallField';

VideocallField.propTypes = {
    changeField: PropTypes.func,
    data: PropTypes.object,
    field: PropTypes.shape({
        description: PropTypes.string,
        disabled: PropTypes.bool,
        id: PropTypes.string,
        inputAttrs: PropTypes.shape({
            list: PropTypes.string,
            shouldRenderField: PropTypes.func,
        }),
        isBulkAction: PropTypes.bool,
        isClearable: PropTypes.bool,
        label: PropTypes.string,
        labelMode: PropTypes.oneOf(['vertical', 'horizontal']),
        mandatory: PropTypes.bool,
        readOnly: PropTypes.bool,
    }),
};

export default VideocallField;
